// The four affinity bands tile the [0,1] score axis contiguously:
//   SCORE = floor + width · (A·Q)
// so affinity strictly dominates ranking while attribute-fit + experience order
// experts WITHIN a band. The backend stores { '<grade>': { band: floor, width } };
// the UI edits the 3 internal BOUNDARIES (the band floors above 0), which keeps
// the partition gapless/overlap-free by construction.

import { TIER_COLORS } from './AffinityNetwork/affinity.constants';

export const AFFINITY_GRADES = ['0', '0.4', '0.7', '1'] as const;
export type AffinityGrade = typeof AFFINITY_GRADES[number];

export interface BandEntry {
  band: number;
  width: number;
}
export type AffinityBands = Record<string, BandEntry>;

// Display metadata per grade — the tier key maps into TIER_COLORS.
export const GRADE_META: Record<
  string,
  { label: string; sublabel: string; tier: 'none' | 'L1' | 'L2' | 'L3' }
> = {
  '0': { label: 'No match', sublabel: 'α = 0', tier: 'none' },
  '0.4': { label: 'L1 broad area', sublabel: 'α = 0.4', tier: 'L1' },
  '0.7': { label: 'L2 family', sublabel: 'α = 0.7', tier: 'L2' },
  '1': { label: 'Exact leaf', sublabel: 'α = 1.0', tier: 'L3' },
};

export const DEFAULT_AFFINITY_BANDS: AffinityBands = {
  '0': { band: 0, width: 0.4 },
  '0.4': { band: 0.4, width: 0.2 },
  '0.7': { band: 0.6, width: 0.15 },
  '1': { band: 0.75, width: 0.25 },
};

// The 3 internal cut points: floors of grades 0.4 / 0.7 / 1 (grade 0's floor is
// pinned at 0, and grade 1's ceiling is pinned at 1.0).
export function boundariesFromBands(bands: AffinityBands): [number, number, number] {
  const b = bands && bands['0.4'] ? bands : DEFAULT_AFFINITY_BANDS;
  return [b['0.4'].band, b['0.7'].band, b['1'].band];
}

export function bandsFromBoundaries(boundaries: number[]): AffinityBands {
  const [b1, b2, b3] = boundaries;
  return {
    '0': { band: 0, width: b1 },
    '0.4': { band: b1, width: b2 - b1 },
    '0.7': { band: b2, width: b3 - b2 },
    '1': { band: b3, width: 1 - b3 },
  };
}

export const round3 = (n: number) => Math.round(n * 1000) / 1000;

export function boundariesEqual(a: number[], b: number[]): boolean {
  return a.length === b.length && a.every((v, i) => Math.abs(v - b[i]) < 1e-6);
}

export function bandFor(alpha: number, bands: AffinityBands) {
  const key = String(Number(alpha));
  const entry = bands[key] ?? DEFAULT_AFFINITY_BANDS[key] ?? { band: 0, width: 1 };
  const meta = GRADE_META[key] ?? GRADE_META['0'];
  return { ...entry, meta, color: TIER_COLORS[meta.tier] };
}

export function gradeColor(grade: number): string {
  if (grade >= 1) return TIER_COLORS.L3;
  if (grade >= 0.7) return TIER_COLORS.L2;
  if (grade >= 0.4) return TIER_COLORS.L1;
  return TIER_COLORS.none;
}
